import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';

import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import BehindTheScenes from './BehindTheScenes';
import { ChannelLogo } from './catalog';
import type { ChannelContent } from './types';
import { useChannelsT } from './useChannelsT';
import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  CheckGlyph,
  delay,
  Stage,
  tone,
  useCopyGap,
  useOpenAgentDemo,
} from '../primitives';

/**
 * The page's H1 is the short label people search for ("WhatsApp AI
 * chatbot"); the display headline under it carries the promise.
 */
export default function ChannelHero({ content }: { content: ChannelContent }) {
  const { t } = useChannelsT();
  const { t: tc } = useLocale('chatbots');
  const gap = useCopyGap();
  const openDemo = useOpenAgentDemo();
  const { hero } = content;

  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto grid max-w-7xl gap-x-12 gap-y-14 px-6 pb-4 pt-10 sm:pt-14 lg:grid-cols-12 lg:items-center lg:px-8 lg:pt-12'>
        <div className='min-w-0 lg:col-span-6'>
          <nav aria-label={t('Breadcrumb')} className={styles.enter}>
            <ol
              className={clsxm(
                'inline-flex max-w-full items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] dark:bg-white/[0.05] dark:ring-white/10',
                tone.quiet,
              )}
            >
              <li>
                <Link
                  href='/chatbots'
                  className='transition-colors hover:text-primary-700 dark:hover:text-primary-300'
                >
                  {t('AI Agents')}
                </Link>
              </li>
              <li aria-hidden='true' className='opacity-50'>
                /
              </li>
              <li aria-current='page' className={clsxm('truncate', tone.ink)}>
                {content.name}
              </li>
            </ol>
          </nav>

          <h1
            className={clsxm(
              styles.enter,
              'mt-8 flex items-center gap-3 text-[15px] font-semibold leading-snug sm:text-base',
              tone.ink,
            )}
            style={delay(40)}
          >
            <span className='flex h-9 w-9 flex-none items-center justify-center rounded-[11px] bg-white shadow-[0_6px_16px_-8px_rgba(15,15,40,0.35)] ring-1 ring-[#0f0f28]/[0.08] dark:ring-white/10'>
              <ChannelLogo channel={content.key} />
            </span>
            {hero.label}
          </h1>

          <p
            className={clsxm(
              styles.display,
              styles.heroTitle,
              styles.enter,
              'mt-5 text-[2.5rem] font-semibold leading-[1.05] sm:text-[3.25rem] sm:leading-[1.03] lg:text-[3.5rem] xl:text-[3.75rem]',
              tone.ink,
            )}
            style={delay(90)}
          >
            {hero.title}
            {gap}
            <span className='text-primary-600 dark:text-primary-400'>
              {hero.accent}
            </span>
          </p>

          <p
            className={clsxm(
              styles.enter,
              'mt-6 max-w-xl text-lg leading-8 sm:text-xl sm:leading-[1.6]',
              tone.body,
            )}
            style={delay(150)}
          >
            {hero.lead}
          </p>

          <div
            className={clsxm(styles.enter, 'mt-8 flex flex-wrap gap-3')}
            style={delay(210)}
          >
            <Link href='/login?preset=bots' className={buttonPrimary}>
              {tc('Build your agent free')}
              <ArrowGlyph />
            </Link>
            <button
              type='button'
              onClick={openDemo}
              className={buttonSecondary}
            >
              <span className='relative flex h-2 w-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none' />
                <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-500' />
              </span>
              {tc('Talk to our agent')}
            </button>
          </div>

          <ul
            className={clsxm(
              styles.enter,
              'mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] font-medium',
              tone.quiet,
            )}
            style={delay(250)}
          >
            {hero.facts.map((fact) => (
              <li key={fact} className='flex items-center gap-1.5'>
                <CheckGlyph className='h-4 w-4 text-emerald-600 dark:text-emerald-400' />
                {fact}
              </li>
            ))}
          </ul>
        </div>

        <div className='-mx-3 min-w-0 sm:mx-0 lg:col-span-6'>
          <Stage
            variant='indigo'
            halftoneAt='100% 0%'
            className={clsxm(
              styles.enter,
              'px-4 py-10 sm:px-10 sm:py-12 lg:px-8 xl:px-12',
            )}
            style={delay(280)}
          >
            <p className='sr-only'>{hero.description}</p>
            <div
              aria-hidden='true'
              className={clsxm(
                'relative mx-auto',
                hero.wide ? 'max-w-[32rem]' : 'max-w-[24rem]',
              )}
            >
              {hero.demo}
              {hero.steps.length > 0 && (
                <div className='relative -mt-10 ms-auto w-[88%] sm:w-[17.5rem] lg:-me-10 xl:-me-16'>
                  <BehindTheScenes steps={hero.steps} />
                </div>
              )}
            </div>
          </Stage>
        </div>
      </div>
    </header>
  );
}
